import React, { useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import OrderProcessingAnimation from './order-processing-image.json';
import { FadeIn } from 'components/loading-screen/FadeIn';

interface AnimatedStep {
    doStep: () => void;
    delay: number;
}

type AnimatedSteps = AnimatedStep[];

type AnimatedStepsConfig = {
    // Number of times to repeat the animation (<=Zero = Infinite)
    loops: number;
};

export const useStepDelay = (steps: AnimatedSteps, configs: AnimatedStepsConfig = { loops: 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [loopsCounter, setLoopsCounter] = useState(1);

    useEffect(() => {
        let timerId: NodeJS.Timeout;

        const executeStep = () => {
            const currentStep = steps[currentIndex];
            if (currentStep) {
                currentStep.doStep();
                timerId = setTimeout(() => {
                    const isLastStep = currentIndex === steps.length - 1;

                    if (isLastStep && configs.loops > 0 && loopsCounter >= configs.loops) {
                        return;
                    }

                    if (isLastStep) {
                        setLoopsCounter(loopsCounter + 1);
                    }

                    setCurrentIndex((prevIndex) => (prevIndex + 1) % steps.length);
                }, currentStep.delay);
            }
        };

        if (isRunning) {
            executeStep();
        }

        return () => clearTimeout(timerId); // Cleanup function to clear timeout
    }, [currentIndex, isRunning, steps, loopsCounter]);

    const start = () => setIsRunning(true);
    const stop = () => setIsRunning(false);

    return { start, stop };
};

const STATUS_MESSAGE_SCREEN_TIME = 3000;

export const BuyNowLoadingAnimation = () => {
    const [animationStatus, setAnimationStatus] = React.useState('');
    const { start, stop } = useStepDelay(
        [
            {
                doStep: () => setAnimationStatus('Sending your order...'),
                delay: STATUS_MESSAGE_SCREEN_TIME,
            },
            {
                doStep: () => setAnimationStatus('Preparing your earnings'),
                delay: STATUS_MESSAGE_SCREEN_TIME,
            },
            {
                doStep: () => setAnimationStatus('Adding final touches'),
                delay: STATUS_MESSAGE_SCREEN_TIME,
            },
        ],
        { loops: 1 }
    );

    useEffect(() => {
        start();
        return () => {
            stop();
        };
    }, []);

    return (
        <FadeIn when={true}>
            <Lottie
                animationData={OrderProcessingAnimation}
                loop={true}
                height={50}
                style={{
                    height: '400px',
                }}
            />
            <div className="font-semibold text-brand-dark text-xl text-center -mt-20 pb-40">
                {animationStatus}
            </div>
        </FadeIn>
    );
};
